import React, {useEffect, useState} from 'react';
import {DirectoryProfile, DirectoryProfileInput, directoryStatus, membersDirectoryApi} from '../../services/membersDirectoryApi.ts';
import {membersError} from '../../services/membersApi.ts';
import {Card, Field, Loading, Notice, primary} from './MembersShared.tsx';

export default function MembersPublicProfile({csrfToken}: {csrfToken: string}) {
  const [profile, setProfile] = useState<DirectoryProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true); setError('');
    void membersDirectoryApi.profile().then(result => {if (current) {setProfile(result.profile); setLoaded(true);}}).catch(err => {if (current) setError(membersError(err));}).finally(() => {if (current) setLoading(false);});
    return () => {current = false;};
  }, [retry]);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const input: DirectoryProfileInput = {
      display_name: String(values.get('display_name')).trim(),
      bio: String(values.get('bio')).trim(),
      photo_url: String(values.get('photo_url')).trim() || null,
      city: String(values.get('city')).trim(),
      state: String(values.get('state')).trim().toUpperCase(),
      specialties: Array.from(new Set(String(values.get('specialties')).split(',').map(value => value.trim()).filter(Boolean))),
      contact_url: String(values.get('contact_url')).trim(),
      consent: values.get('consent') === 'on',
      publish_requested: values.get('publish_requested') === 'on',
      revision: profile?.revision || 0,
    };
    if (input.specialties.length > 8 || input.specialties.some(value => value.length < 2 || value.length > 60)) {
      setError('Informe até 8 especialidades, com 2 a 60 caracteres cada.'); return;
    }
    setSaving(true); setSaved(false); setError('');
    try {
      const result = await membersDirectoryApi.save(input, csrfToken);
      setProfile(result.profile); setSaved(true);
    } catch(err) {setError(membersError(err));} finally {setSaving(false);}
  }
  return (
    <Card>
      <div className="mb-6 space-y-3">
        <h2 className="text-lg font-semibold">Meu perfil público</h2>
        <p className="members-secondary text-sm leading-relaxed">Oferece serviços como marketing ou tráfego pago? Divulgar seu trabalho no catálogo é opcional: você pode indicar clínicas sem publicar um perfil. Estes dados são separados do cadastro financeiro e passam por revisão antes da publicação.</p>
        {profile && <p className="text-sm">Situação: <strong>{directoryStatus(profile.status)}</strong></p>}
        {profile?.status === 'published' && <Notice>Ao salvar alterações, seu perfil sai do catálogo até uma nova revisão.</Notice>}
      </div>
      {loading ? <Loading /> : !loaded ? <Notice error>{error} <button className="underline" onClick={() => setRetry(value => value + 1)}>Tentar novamente</button></Notice> : (
        <form onSubmit={submit} className="space-y-5" key={profile?.revision || 0}>
          <fieldset disabled={saving} className="space-y-5">
            <Field label="Nome público" name="display_name" defaultValue={profile?.display_name} required minLength={2} maxLength={160} />
            <label className="block space-y-2 text-sm"><span>Sobre seus serviços *</span><textarea className="members-input w-full" name="bio" rows={5} defaultValue={profile?.bio} required minLength={20} maxLength={2000} /></label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Cidade" name="city" defaultValue={profile?.city} required minLength={2} maxLength={100} />
              <Field label="Estado (UF)" name="state" defaultValue={profile?.state} required minLength={2} maxLength={2} pattern="[A-Za-z]{2}" />
            </div>
            <Field label="Especialidades, separadas por vírgula" name="specialties" defaultValue={profile?.specialties.join(', ')} required maxLength={500} placeholder="Ex.: Tráfego pago, Marketing digital, Captação de pacientes" />
            <Field label="Link da foto (HTTPS)" name="photo_url" type="url" pattern="https://.*" defaultValue={profile?.photo_url || ''} maxLength={2048} />
            <Field label="Link público de contato (HTTPS)" name="contact_url" type="url" pattern="https://.*" defaultValue={profile?.contact_url} required maxLength={2048} placeholder="https://..." />
            <p className="members-muted text-xs">Use um link de WhatsApp, site ou outro canal pelo qual deseja receber contatos.</p>
            <label className="flex items-start gap-3 text-sm leading-relaxed"><input type="checkbox" name="consent" required defaultChecked={profile?.consent || false} className="mt-1" /><span>Autorizo a divulgação do meu nome público, foto, apresentação, cidade, especialidades e link de contato no catálogo de parceiros da Sminy.</span></label>
            <label className="flex items-start gap-3 text-sm leading-relaxed"><input type="checkbox" name="publish_requested" defaultChecked={profile?.publish_requested || false} className="mt-1" /><span>Quero enviar este perfil para revisão e publicação.</span></label>
          </fieldset>
          {error && <Notice error>{error} <button type="button" onClick={() => setRetry(value => value + 1)} className="underline">Recarregar perfil</button></Notice>}
          {saved && <Notice>{profile?.publish_requested ? 'Perfil enviado para revisão. Ele aparecerá no catálogo após a publicação pela Sminy.' : 'Rascunho salvo. Marque o envio para revisão quando estiver pronto.'}</Notice>}
          <button disabled={saving || loading} className={primary}>{saving ? 'Salvando...' : 'Salvar perfil público'}</button>
        </form>
      )}
    </Card>
  );
}
